import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { business } from "../content/site";
import { trackEvent } from "../lib/analytics";
import { useLang, useLp } from "../i18n/lang";
import { useCopy } from "../i18n/copy";

export const Logo = () => {
  const lp = useLp();
  return (
    <Link to={lp("/")} data-testid="nav-brand-logo" className="flex items-center group shrink-0" aria-label="MideFinca - inicio">
      <img
        src="/brand/midefinca-logo-nav.png"
        alt="MideFinca"
        width="923"
        height="192"
        className="w-auto h-12 lg:h-10 xl:h-12"
      />
    </Link>
  );
};

const LangSwitch = ({ suffix = "desktop" }) => {
  const lang = useLang();
  const n = useCopy("nav");
  const { pathname } = useLocation();
  const esPath = lang === "en" ? pathname.replace(/^\/en(?=\/|$)/, "") || "/" : pathname;
  const enPath = lang === "en" ? pathname : `/en${pathname}`;
  const item = "font-mono text-xs tracking-wider transition-colors";
  const active = "text-terracotta font-bold";
  const idle = "text-ink/70 hover:text-terracotta";
  return (
    <div className="flex items-center gap-1.5" aria-label={n.langAria} data-testid={`lang-switch-${suffix}`}>
      {lang === "es" ? (
        <span className={`${item} ${active}`} aria-current="true">ES</span>
      ) : (
        <Link to={esPath} data-testid={`lang-switch-es-${suffix}`} className={`${item} ${idle}`}>ES</Link>
      )}
      <span className="text-slate-300 text-xs" aria-hidden="true">/</span>
      {lang === "en" ? (
        <span className={`${item} ${active}`} aria-current="true">EN</span>
      ) : (
        <Link to={enPath} data-testid={`lang-switch-en-${suffix}`} className={`${item} ${idle}`}>EN</Link>
      )}
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const n = useCopy("nav");
  const lp = useLp();
  const lang = useLang();

  const items = [
    { label: n.home, href: "/" },
    { label: n.services, href: "/servicios/" },
    { label: n.problems, href: "/problemas/" },
    { label: n.guides, href: "/guias/" },
    { label: n.professionals, href: "/profesionales/" },
    { label: n.about, href: "/sobre-midefinca/" },
    { label: n.contact, href: "/contacto/" },
  ];

  useEffect(() => setOpen(false), [location.pathname]);

  const isActive = (href) => {
    const target = lp(href);
    return href !== "/" && location.pathname.startsWith(target);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-paper/85 border-b border-slate-200/80">
      <div className="container-site flex items-center justify-between h-16 lg:h-[72px]">
        <Logo />
        <nav className="hidden lg:flex items-center gap-3 xl:gap-6" aria-label={n.mainAria}>
          {items.slice(1).map((item) => (
            <Link
              key={item.href}
              to={lp(item.href)}
              data-testid={`nav-link-${item.href.replaceAll("/", "")}`}
              className={`text-[12px] xl:text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors hover:text-terracotta ${
                isActive(item.href) ? "text-terracotta" : "text-ink/80"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3 xl:gap-5">
          <LangSwitch key={lang} suffix="desktop" />
          <a
            href={business.phoneHref}
            data-testid="nav-phone-link"
            onClick={() => trackEvent("phone_click", { location: "header" })}
            className="hidden 2xl:flex items-center gap-1.5 whitespace-nowrap font-mono text-xs tracking-wider text-ink/70 hover:text-terracotta transition-colors"
          >
            <Phone size={13} aria-hidden="true" /> {business.phoneDisplay}
          </a>
          <Link
            to={lp("/contacto/")}
            data-testid="nav-cta-budget"
            onClick={() => trackEvent("cta_budget_click", { location: "header" })}
            className="btn-primary !px-3.5 xl:!px-5 !py-2.5 whitespace-nowrap"
          >
            {n.quote}
          </Link>
        </div>
        <div className="lg:hidden flex items-center gap-4">
          <LangSwitch key={`m-${lang}`} suffix="mobile" />
          <button
            data-testid="nav-mobile-toggle"
            className="p-2 -mr-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? n.closeMenu : n.openMenu}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-slate-200 bg-paper" aria-label={n.mobileAria}>
          <div className="container-site py-4 flex flex-col">
            {items.map((item) => (
              <Link
                key={item.href}
                to={lp(item.href)}
                className="py-3.5 text-base font-medium text-ink border-b hairline last:border-0"
              >
                {item.label}
              </Link>
            ))}
            <Link to={lp("/contacto/")} data-testid="nav-mobile-cta" className="btn-primary mt-4 mb-2">
              {n.quote}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
